import React, { useEffect, useState, useMemo } from "react";
import {
  useNavigate
} from "react-router-dom";
import {
  Plus,
  ArrowLeft,
  Building2
} from "lucide-react";

import {
  createExamRoom,
  getAllExamRooms,
  deleteExamRoom
} from "../../api/examsschedule/examSeatingApi";
import {
  createExamRoomPortal,
  getAllExamRoomsPortal,
  deleteExamRoomPortal
} from "../../api/examsschedule/examSeatingPortalApi";
import {
  useStaffAuth
} from "../../context/StaffAuthContext";
import {
  pickList,
  toId
} from "../../utils/examsschedule/examHelpers";
import {
  Button,
  Input,
  Select,
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Table,
  Badge,
  EmptyState
} from "../../components/ui";
import {
  useToast
} from "../../context/ToastContext";

const RoomList = ({ isPortal = false }) => {
  const navigate = useNavigate();
  const staffAuth = useStaffAuth();
  const canManage = isPortal
    ? staffAuth?.hasPermission?.("EXAM_MANAGEMENT_FULL")
    : true;

  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [activeOnly, setActiveOnly] = useState(true);
  const [form, setForm] = useState({
    roomNo: "",
    name: "",
    totalBenches: "",
    capacity: "",
    seatsPerBench: "1",
    building: "",
    floor: "",
  });

  const toast = useToast();

  const examsPath = isPortal ? "/staff-panel/exam/exams" : "/exam/exams";

  const load = async () => {
    try {
      setLoading(true);
      const res = isPortal
        ? await getAllExamRoomsPortal(
            activeOnly ? { isActive: "true" } : {}
          )
        : await getAllExamRooms(activeOnly ? { isActive: "true" } : {});
      setRooms(pickList(res, ["rooms", "data"]));
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to load rooms");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPortal, activeOnly]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.roomNo || !form.totalBenches) {
      toast.error("Room no and total benches are required");
      return;
    }
    try {
      setSubmitting(true);
      const seats = Number(form.seatsPerBench) || 1;
      const benches = Number(form.totalBenches);
      const payload = {
        roomNo: form.roomNo.trim(),
        name: form.name || "",
        totalBenches: benches,
        capacity: form.capacity
          ? Number(form.capacity)
          : benches * seats,
        seatsPerBench: seats,
        building: form.building || "",
        floor: form.floor || "",
      };
      if (isPortal) await createExamRoomPortal(payload);
      else await createExamRoom(payload);
      toast.success("Room created");
      setForm({
        roomNo: "",
        name: "",
        totalBenches: "",
        capacity: "",
        seatsPerBench: "1",
        building: "",
        floor: "",
      });
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Create failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeactivate = async (id) => {
    if (!window.confirm("Deactivate this room?")) return;
    try {
      if (isPortal) await deleteExamRoomPortal(id);
      else await deleteExamRoom(id);
      toast.success("Room deactivated");
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed");
    }
  };

  const activeFilterOptions = [
    { label: "Active only", value: "active" },
    { label: "All rooms", value: "all" },
  ];

  const seatsPerBenchOptions = [
    { label: "1", value: "1" },
    { label: "2", value: "2" },
  ];

  const columns = [
    {
      key: "roomNo",
      header: "Room",
      render: (r) => (
        <div className="flex items-center gap-2">
          <span className="font-medium text-neutral-900 dark:text-neutral-100">{r.roomNo}</span>
          {r.name && <span className="text-neutral-500 dark:text-neutral-400">— {r.name}</span>}
          {r.isActive === false && <Badge variant="danger">Inactive</Badge>}
        </div>
      ),
    },
    { key: "totalBenches", header: "Benches" },
    {
      key: "seatsPerBench",
      header: "Seats/Bench",
      render: (r) => r.seatsPerBench || 1,
    },
    { key: "capacity", header: "Capacity" },
    {
      key: "location",
      header: "Location",
      render: (r) =>
        [r.building, r.floor].filter(Boolean).join(" · ") || "—",
    },
    {
      key: "actions",
      header: "Actions",
      align: "right",
      render: (r) =>
        canManage && r.isActive !== false ? (
          <Button
            type="button"
            variant="ghost"
            size="xs"
            onClick={() => handleDeactivate(toId(r))}
            className="text-danger-500 hover:text-danger-700 hover:bg-danger-50"
          >
            Deactivate
          </Button>
        ) : null,
    },
  ];

  const tableRows = useMemo(
    () => rooms.map((r) => ({ ...r, id: toId(r) })),
    [rooms]
  );

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-6">
      {/* Back Button & Header Row */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <button
            type="button"
            onClick={() => navigate(examsPath)}
            className="inline-flex items-center gap-1 text-sm font-medium text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100 transition-colors mb-2"
          >
            <ArrowLeft className="h-4 w-4" /> Back to exams
          </button>
          <h1 className="text-2xl font-semibold text-neutral-900 dark:text-neutral-100">Exam Rooms</h1>
          <p className="mt-0.5 text-sm text-neutral-500 dark:text-neutral-400">
            Master list for FULL-profile seating plans
          </p>
        </div>
        <div className="w-full sm:w-48">
          <Select
            options={activeFilterOptions}
            value={activeOnly ? "active" : "all"}
            onChange={(val) => setActiveOnly(val === "active")}
          />
        </div>
      </div>

      {/* Alerts */}
      
      

      {/* Add Room Form Card */}
      {canManage && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base font-semibold text-neutral-900 dark:text-neutral-100 flex items-center gap-2">
              <Plus className="h-4 w-4 text-primary-900 dark:text-primary-300 shrink-0" />
              Add Room
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 items-end">
                <Input
                  label="Room No *"
                  placeholder="e.g. 101"
                  value={form.roomNo}
                  onChange={(e) => setForm({ ...form, roomNo: e.target.value })}
                  required
                />
                <Input
                  label="Name (optional)"
                  placeholder="e.g. Hall A"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
                <Input
                  type="number"
                  min={1}
                  label="Total Benches *"
                  placeholder="e.g. 20"
                  value={form.totalBenches}
                  onChange={(e) =>
                    setForm({ ...form, totalBenches: e.target.value })
                  }
                  required
                />
                <Select
                  label="Seats Per Bench"
                  options={seatsPerBenchOptions}
                  value={form.seatsPerBench}
                  onChange={(val) =>
                    setForm({ ...form, seatsPerBench: val })
                  }
                />
                <Input
                  type="number"
                  min={1}
                  label="Capacity"
                  placeholder="Auto if empty"
                  value={form.capacity}
                  onChange={(e) =>
                    setForm({ ...form, capacity: e.target.value })
                  }
                />
                <Input
                  label="Building"
                  placeholder="e.g. Science Block"
                  value={form.building}
                  onChange={(e) =>
                    setForm({ ...form, building: e.target.value })
                  }
                />
                <Input
                  label="Floor"
                  placeholder="e.g. 2nd Floor"
                  value={form.floor}
                  onChange={(e) => setForm({ ...form, floor: e.target.value })}
                />
                <div className="flex items-end">
                  <Button
                    type="submit"
                    variant="primary"
                    fullWidth
                    isLoading={submitting}
                  >
                    Create Room
                  </Button>
                </div>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Rooms Table Card */}
      <Card>
        <CardContent className="p-0">
          <Table
            columns={columns}
            data={tableRows}
            isLoading={loading}
            emptyState={
              <EmptyState
                icon={Building2}
                title="No rooms found"
                description="Create an exam room above to set up seating capacity."
              />
            }
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default RoomList;